/**
 * publicLegalRoutes.jsx — Routes publiques (légales + support), bypass auth gate.
 *
 * Extrait de App.jsx (Sprint A legal-EN) : isole la map pathname → page et son
 * helper de résolution, pour repasser App.jsx sous le cap 500 L et seater l'ajout
 * des routes /en/legal/* (Sprint B, D-LEGAL-EN-2).
 *
 * Requis pour App Store Guideline 5.1.1 et Google Play Data Safety : privacy
 * policy, CGU et support doivent être accessibles sans authentification.
 *
 * Comportement strictement identique à l'implémentation inline précédente :
 * mêmes pathnames → mêmes composants, même wrapper + Suspense, même gating
 * dev-only de /vet-pdf-demo.
 */
import React, { Suspense, lazy } from 'react';
import legalLabels from '../content/legal/labels';

// Pages publiques chargées à la demande (PERF-07). Surface de routage distincte
// du routage in-app d'App.jsx ; webpack dédupe les chunks sous-jacents.
const MentionsPage   = lazy(() => import('../settings/MentionsPage'));
const CGUPage        = lazy(() => import('../settings/CGUPage'));
const PrivacyPage    = lazy(() => import('../settings/PrivacyPage'));
const CookiesPage    = lazy(() => import('../settings/CookiesPage'));
const ModerationPage = lazy(() => import('../settings/ModerationPage'));
const SupportPage    = lazy(() => import('../settings/SupportPage'));
// L6c.4 / D90 — Page demo PDF clinique vétérinaire, DEV-ONLY.
const PdfDemoPage    = lazy(() => import('../vetpro/pdf/__demo__/PdfDemoPage'));

// Map pathname → thunk de rendu. Construite par appel pour capturer darkMode.
function buildPublicLegalRoutes(darkMode) {
  return {
    '/legal/mentions':   () => <MentionsPage   darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    '/legal/cgu':        () => <CGUPage        darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    '/legal/privacy':    () => <PrivacyPage    darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    '/legal/cookies':    () => <CookiesPage    darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    '/legal/moderation': () => <ModerationPage darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    '/support':          () => <SupportPage    darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    // Pages légales EN P0 (D-LEGAL-EN-2/3, Sprint B2) : mêmes pages lazy rendues
    // en anglais (lang="en"), même wrapper + bypass auth que le FR. Le contenu EN
    // vient de content/legal/*/en.js ; la langue ne provient QUE de l'URL (D-5).
    '/en/legal/privacy': () => <PrivacyPage    lang="en" darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    '/en/legal/cgu':     () => <CGUPage        lang="en" darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    // Cookies / Mentions / Modération EN (i18n légal 1.11, brouillon draft:true →
    // bandeau auto). Mêmes variantes publiques que leur FR ; contenu EN via
    // content/legal/*/en.js ; langue = URL (D-5).
    '/en/legal/cookies':    () => <CookiesPage    lang="en" darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    '/en/legal/mentions':   () => <MentionsPage   lang="en" darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    '/en/legal/moderation': () => <ModerationPage lang="en" darkMode={darkMode} onBack={() => { window.location.href = '/'; }} />,
    // En production (NODE_ENV='production' du build CRA), la route n'est pas
    // exposée → l'URL retombe sur la SPA fallback (logique normale).
    ...(process.env.NODE_ENV === 'development' && {
      '/vet-pdf-demo': () => <PdfDemoPage />,
    }),
  };
}

/**
 * Résout un pathname en élément de page publique rendu (div + Suspense), ou
 * renvoie null si le pathname n'est pas une route publique légale.
 */
export function resolvePublicLegalRoute(pathname, { darkMode }) {
  const route = buildPublicLegalRoutes(darkMode)[pathname];
  if (!route) return null;
  // Langue = segment d'URL uniquement (D-LEGAL-EN-5) ; pilote le seul label du
  // fallback Suspense (« Chargement… » / « Loading… »). Repli fr.
  const lang = pathname.startsWith('/en/') ? 'en' : 'fr';
  const loading = (legalLabels[lang] || legalLabels.fr).loading;
  return (
    <div className="App" style={{ minHeight: '100vh', background: darkMode ? '#1E1E32' : 'linear-gradient(135deg, #E6F0FF 0%, #F0F9FF 100%)' }}>
      <Suspense fallback={<div style={{ textAlign: 'center', padding: '60px', color: darkMode ? '#E8EDF2' : '#4A5568' }}>{loading}</div>}>
        {route()}
      </Suspense>
    </div>
  );
}
